import { Inject, Injectable } from '@angular/core'
import { BootstrapData, MediaRootPermission, MediaFileBundle } from 'elements-sdk'
import { APIService } from 'api/module'
import { BOOTSTRAP_DATA } from 'api/providers'
import { TranslateService } from '@ngx-translate/core'
import { SocketService } from 'common/util/module'
import { PermissionSchema } from 'main/permissionModel'
import { IdentityService } from 'main/services/identity.service'
import { MediaRootPermissionPermissionFields, MediaRootPermissionUIVisibilityFields } from 'media/constants'

@Injectable({ providedIn: 'root' })
export class MediaPermissionsService {
  permissions: MediaRootPermission[]
  private initialized = false

  readonly schema: PermissionSchema = { permissionGroups: [] }

  constructor (
    private identity: IdentityService,
    private socket: SocketService,
    private api: APIService,
    private translate: TranslateService,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
  ) {
    this.initConstants()
    this.permissions = bootstrapData.mediaRootPermissions
    this.permissions.reverse()
    this.maybeInit()
    identity.identityUpdated$.subscribe(() => {
      this.maybeInit()
    })
  }

  private maybeInit () {
    if (!this.identity.hasPermission('media:access') || this.initialized) {
      return
    }
    this.initialized = true
    this.socket.subject('media-root-permission:updated').subscribe(() => this.reloadPermissions())
    this.reloadPermissions()
  }

  async reloadPermissions (): Promise<void> {
    this.permissions = await this.api.mediaLibrary.getMyResolvedMediaRootPermissions({})
    this.permissions.reverse()
  }

  getPermissionForPath (path: string): MediaRootPermission|null {
    return this.permissions.find(x => x.fullPath === path || path.startsWith(x.fullPath + '/')) ?? null
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPermissionForFile (file: any): MediaRootPermission|null {
    return this.getPermissionForPath(file.fullPath ?? file.full_path)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPermissionForBundle (bundle: MediaFileBundle): MediaRootPermission|null {
    return this.getPermissionForFile(bundle.mainfile)
  }

  mergePermissions (permissions: MediaRootPermission[]): MediaRootPermission {
    const merged: Partial<MediaRootPermission> = {}

    for (const sp of MediaRootPermissionPermissionFields) {
      merged[sp] = true
    }
    for (const sp of MediaRootPermissionUIVisibilityFields) {
      merged[sp] = true
    }

    for (const p of permissions) {
      for (const sp of MediaRootPermissionPermissionFields) {
        merged[sp] &&= p[sp]
      }
      for (const sp of MediaRootPermissionUIVisibilityFields) {
        merged[sp] &&= p[sp]
      }
    }

    return merged as MediaRootPermission
  }

  private initConstants () {
    this.schema.permissionGroups = [
      {
        name: 'Access',
        items: [
          {
            id: 'allowRead',
            name: this.translate.instant('View files'),
          },
          {
            id: 'allowWriteFs',
            name: this.translate.instant('Write files'),
            require: ['allowRead'],
          },
          {
            id: 'allowWriteDb',
            name: this.translate.instant('Write metadata'),
            require: ['allowRead'],
          },
          {
            id: 'allowCreate',
            name: this.translate.instant('Create files and folders'),
            require: ['allowWriteFs'],
          },
          {
            id: 'allowOriginalDownload',
            name: this.translate.instant('Download original files'),
            require: ['allowRead'],
          },
          {
            id: 'allowProxyDownload',
            name: this.translate.instant('Download proxies'),
            require: ['allowRead'],
          },
          {
            id: 'allowUpload',
            name: this.translate.instant('Upload'),
          },
          {
            id: 'allowSharing',
            name: this.translate.instant('Re-share'),
            require: ['allowRead'],
          },
          {
            id: 'allowDeleteFs',
            name: this.translate.instant('Delete files'),
            require: ['allowWriteFs'],
          },
          {
            id: 'allowDeleteDb',
            name: this.translate.instant('Delete metadata'),
            require: ['allowWriteDb'],
          },
        ],
      },
      {
        name: 'UI',
        items: [
          {
            id: 'showComments',
            name: this.translate.instant('Comments'),
          },
          {
            id: 'showTags',
            name: this.translate.instant('Tags'),
          },
          {
            id: 'showLocations',
            name: this.translate.instant('Locations'),
          },
          {
            id: 'showCustomFields',
            name: this.translate.instant('Custom fields'),
          },
          {
            id: 'showRatings',
            name: this.translate.instant('Ratings'),
          },
          {
            id: 'showSubclips',
            name: this.translate.instant('Subclips'),
          },
          {
            id: 'showSubtitles',
            name: this.translate.instant('Subtitles'),
          },
          {
            id: 'showMarkers',
            name: this.translate.instant('Markers'),
          },
          {
            id: 'showHistory',
            name: this.translate.instant('History'),
          },
        ],
      },
    ]
    if (this.bootstrapData.aiConnections.length) {
      this.schema.permissionGroups[1].items.push({
        id: 'showAiMetadata',
        name: this.translate.instant('AI metadata'),
      })
    }
  }
}
